import type { AgentRole, WorkflowStep, WorkflowStepInput } from '../api/admin';
import type { RoleOption } from '../lib/use-agent-roles';

// ワークフロー工程（工程フロー図）の編集 UI。チーム本体・テンプレートの両方で使う共通部品。
// 「いつ（工程名）→ 誰が（担当役割）→ 何をする（実行アクション）→ 完了後どこへ（渡し先）→
//  質問が出たら（質問時の動き）」を 1 工程として順序付きで並べる。
//
// 設計（#209 / タスク#211）: action / handoffTo / onQuestion は当面フリーテキスト。
// 仕様が大規模修正中のため構造化はせず、まず「どういうときにどう動くか」を書ける枠を用意する。

// 編集中の 1 工程（フォーム用。空文字を許容し、保存時に正規化する）。
export interface WorkflowDraft {
  name: string;
  ownerRole: string; // 'unset' = 担当なし
  action: string;
  handoffTo: string;
  onQuestion: string;
}

// サーバの WorkflowStep → 編集用ドラフト。
export function draftFromStep(s: WorkflowStep): WorkflowDraft {
  return {
    name: s.name,
    ownerRole: s.ownerRole || 'unset',
    action: s.action ?? '',
    handoffTo: s.handoffTo ?? '',
    onQuestion: s.onQuestion ?? '',
  };
}

// 空の新規工程。担当役割の初期値は呼び出し側で渡す（既定は担当なし）。
export function emptyWorkflowDraft(ownerRole = 'unset'): WorkflowDraft {
  return { name: '', ownerRole, action: '', handoffTo: '', onQuestion: '' };
}

// 編集用ドラフト → 保存入力。工程名が空の行は捨てる（誤って増えた空行を保存しない）。
export function draftsToInput(drafts: WorkflowDraft[]): WorkflowStepInput[] {
  return drafts
    .filter((d) => d.name.trim())
    .map((d) => ({
      name: d.name.trim(),
      ownerRole: d.ownerRole || 'unset',
      action: d.action.trim() || null,
      handoffTo: d.handoffTo.trim() || null,
      onQuestion: d.onQuestion.trim() || null,
    }));
}

const labelStyle = { fontSize: 12, fontWeight: 600, color: '#475569' } as const;
const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 3 } as const;

export function WorkflowStepsEditor({
  steps,
  onChange,
  roleOptions,
  roleLabelOf,
}: {
  steps: WorkflowDraft[];
  onChange: (next: WorkflowDraft[]) => void;
  roleOptions: RoleOption[];
  roleLabelOf: (role: AgentRole) => string;
}) {
  const setAt = (i: number, patch: Partial<WorkflowDraft>) =>
    onChange(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => onChange(steps.filter((_, idx) => idx !== i));
  const add = () => onChange([...steps, emptyWorkflowDraft()]);
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {steps.length === 0 ? (
        <p className="k-muted" style={{ fontSize: 13, margin: '2px 0' }}>
          まだ工程がありません。「＋ 工程を追加」で、メンバーが「どういうときにどう動くか」を 1 工程ずつ並べてください。
        </p>
      ) : (
        steps.map((s, i) => (
          <div
            key={i}
            style={{
              border: '1px solid #e7ebf0',
              borderRadius: 10,
              padding: '10px 12px',
              background: '#fff',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {/* ヘッダ行: 工程番号 + 並べ替え/削除 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                className="k-badge"
                style={{ background: '#eef2ff', color: '#3730a3', minWidth: 46, textAlign: 'center' }}
              >
                工程 {i + 1}
              </span>
              <div style={{ flex: 1 }} />
              <button
                className="btn-ghost"
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="上へ"
                style={{ padding: '4px 8px' }}
              >
                ↑
              </button>
              <button
                className="btn-ghost"
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === steps.length - 1}
                aria-label="下へ"
                style={{ padding: '4px 8px' }}
              >
                ↓
              </button>
              <button
                className="btn-danger"
                type="button"
                onClick={() => remove(i)}
                aria-label="この工程を削除"
                style={{ padding: '4px 8px' }}
              >
                ✕
              </button>
            </div>

            {/* 工程名 + 担当役割 */}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ ...fieldWrap, flex: 1 }}>
                <label style={labelStyle}>工程名</label>
                <input
                  placeholder="例: 設計 / 実装 / レビュー"
                  value={s.name}
                  onChange={(e) => setAt(i, { name: e.target.value })}
                />
              </div>
              <div style={{ ...fieldWrap, width: 180 }}>
                <label style={labelStyle}>担当役割</label>
                <select value={s.ownerRole} onChange={(e) => setAt(i, { ownerRole: e.target.value })}>
                  <option value="unset">（担当なし）</option>
                  {/* 既存値がマスタに無い（無効化済み等）場合も値が消えないよう先頭に補う */}
                  {s.ownerRole !== 'unset' && !roleOptions.some((o) => o.value === s.ownerRole) && (
                    <option value={s.ownerRole}>{roleLabelOf(s.ownerRole)}</option>
                  )}
                  {roleOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 実行アクション */}
            <div style={fieldWrap}>
              <label style={labelStyle}>実行アクション（このとき何をする）</label>
              <input
                placeholder="例: MCP でタスクを登録する"
                value={s.action}
                onChange={(e) => setAt(i, { action: e.target.value })}
              />
            </div>

            {/* 渡し先 + 質問時の動き */}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ ...fieldWrap, flex: 1 }}>
                <label style={labelStyle}>完了後の渡し先</label>
                <input
                  placeholder="例: 実装者へ渡す"
                  value={s.handoffTo}
                  onChange={(e) => setAt(i, { handoffTo: e.target.value })}
                />
              </div>
              <div style={{ ...fieldWrap, flex: 1 }}>
                <label style={labelStyle}>質問が出たときの動き</label>
                <input
                  placeholder="例: チャットに報告する"
                  value={s.onQuestion}
                  onChange={(e) => setAt(i, { onQuestion: e.target.value })}
                />
              </div>
            </div>
          </div>
        ))
      )}
      <button
        className="btn-ghost"
        type="button"
        onClick={add}
        style={{ alignSelf: 'flex-start', marginTop: 2 }}
      >
        ＋ 工程を追加
      </button>
    </div>
  );
}
